/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  AddMediaCollectionItemsDto,
  ApiErrorDto,
  CreateMediaCollectionDto,
  ListMediaCollectionEntriesParams,
  MediaCollectionDto,
  MediaCollectionEntriesDto,
  MediaCollectionPreviewDto,
  PreviewMediaCollectionDto,
  UpdateMediaCollectionDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The library's collections, oldest first.
 */
export const listMediaCollections = (id: string, signal?: AbortSignal) => {
  return customInstance<MediaCollectionDto[]>({
    url: `/api/v1/workspaces/${id}/media-collections`,
    method: 'GET',
    signal,
  });
};

export const getListMediaCollectionsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/media-collections`] as const;
};

export const getListMediaCollectionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMediaCollections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMediaCollections>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMediaCollectionsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMediaCollections>>> = ({
    signal,
  }) => listMediaCollections(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMediaCollections>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListMediaCollectionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMediaCollections>>
>;
export type ListMediaCollectionsQueryError = ErrorType<ApiErrorDto>;

export function useListMediaCollections<
  TData = Awaited<ReturnType<typeof listMediaCollections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMediaCollections>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMediaCollections>>,
          TError,
          Awaited<ReturnType<typeof listMediaCollections>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMediaCollections<
  TData = Awaited<ReturnType<typeof listMediaCollections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMediaCollections>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMediaCollections>>,
          TError,
          Awaited<ReturnType<typeof listMediaCollections>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMediaCollections<
  TData = Awaited<ReturnType<typeof listMediaCollections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMediaCollections>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The library's collections, oldest first.
 */

export function useListMediaCollections<
  TData = Awaited<ReturnType<typeof listMediaCollections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMediaCollections>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListMediaCollectionsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create a hand-picked collection (optionally with assets) or a smart one (a rule).
 */
export const createMediaCollection = (
  id: string,
  createMediaCollectionDto: BodyType<CreateMediaCollectionDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaCollectionDto>({
    url: `/api/v1/workspaces/${id}/media-collections`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createMediaCollectionDto,
    signal,
  });
};

export const getCreateMediaCollectionMutationKey = () =>
  ['createMediaCollection'] as const;

export const getCreateMediaCollectionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMediaCollection>>,
    TError,
    CreateMediaCollectionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMediaCollection>>,
  TError,
  CreateMediaCollectionMutationVariables,
  TContext
> => {
  const mutationKey = getCreateMediaCollectionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMediaCollection>>,
    CreateMediaCollectionMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createMediaCollection(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMediaCollectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMediaCollection>>
>;
export type CreateMediaCollectionMutationBody = BodyType<CreateMediaCollectionDto>;
export type CreateMediaCollectionMutationError = ErrorType<ApiErrorDto>;
export type CreateMediaCollectionMutationVariables = {
  id: string;
  data: BodyType<CreateMediaCollectionDto>;
};

/**
 * @summary Create a hand-picked collection (optionally with assets) or a smart one (a rule).
 */
export const useCreateMediaCollection = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createMediaCollection>>,
      TError,
      CreateMediaCollectionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createMediaCollection>>,
  TError,
  CreateMediaCollectionMutationVariables,
  TContext
> => {
  return useMutation(getCreateMediaCollectionMutationOptions(options), queryClient);
};
/**
 * @summary How many assets a rule would keep right now, and the first few - before saving it.
 */
export const previewMediaCollection = (
  id: string,
  previewMediaCollectionDto: BodyType<PreviewMediaCollectionDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaCollectionPreviewDto>({
    url: `/api/v1/workspaces/${id}/media-collections/preview`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: previewMediaCollectionDto,
    signal,
  });
};

export const getPreviewMediaCollectionMutationKey = () =>
  ['previewMediaCollection'] as const;

export const getPreviewMediaCollectionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewMediaCollection>>,
    TError,
    PreviewMediaCollectionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof previewMediaCollection>>,
  TError,
  PreviewMediaCollectionMutationVariables,
  TContext
> => {
  const mutationKey = getPreviewMediaCollectionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof previewMediaCollection>>,
    PreviewMediaCollectionMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return previewMediaCollection(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PreviewMediaCollectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof previewMediaCollection>>
>;
export type PreviewMediaCollectionMutationBody = BodyType<PreviewMediaCollectionDto>;
export type PreviewMediaCollectionMutationError = ErrorType<ApiErrorDto>;
export type PreviewMediaCollectionMutationVariables = {
  id: string;
  data: BodyType<PreviewMediaCollectionDto>;
};

/**
 * @summary How many assets a rule would keep right now, and the first few - before saving it.
 */
export const usePreviewMediaCollection = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof previewMediaCollection>>,
      TError,
      PreviewMediaCollectionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof previewMediaCollection>>,
  TError,
  PreviewMediaCollectionMutationVariables,
  TContext
> => {
  return useMutation(getPreviewMediaCollectionMutationOptions(options), queryClient);
};
/**
 * @summary Rename a collection, describe it, or change a smart collection's rule.
 */
export const updateMediaCollection = (
  id: string,
  collectionId: string,
  updateMediaCollectionDto: BodyType<UpdateMediaCollectionDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaCollectionDto>({
    url: `/api/v1/workspaces/${id}/media-collections/${collectionId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateMediaCollectionDto,
    signal,
  });
};

export const getUpdateMediaCollectionMutationKey = () =>
  ['updateMediaCollection'] as const;

export const getUpdateMediaCollectionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMediaCollection>>,
    TError,
    UpdateMediaCollectionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMediaCollection>>,
  TError,
  UpdateMediaCollectionMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateMediaCollectionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMediaCollection>>,
    UpdateMediaCollectionMutationVariables
  > = (props) => {
    const { id, collectionId, data } = props ?? {};

    return updateMediaCollection(id, collectionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMediaCollectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMediaCollection>>
>;
export type UpdateMediaCollectionMutationBody = BodyType<UpdateMediaCollectionDto>;
export type UpdateMediaCollectionMutationError = ErrorType<ApiErrorDto>;
export type UpdateMediaCollectionMutationVariables = {
  id: string;
  collectionId: string;
  data: BodyType<UpdateMediaCollectionDto>;
};

/**
 * @summary Rename a collection, describe it, or change a smart collection's rule.
 */
export const useUpdateMediaCollection = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateMediaCollection>>,
      TError,
      UpdateMediaCollectionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateMediaCollection>>,
  TError,
  UpdateMediaCollectionMutationVariables,
  TContext
> => {
  return useMutation(getUpdateMediaCollectionMutationOptions(options), queryClient);
};
/**
 * @summary Delete a collection. Its assets stay in the library.
 */
export const deleteMediaCollection = (
  id: string,
  collectionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/media-collections/${collectionId}`,
    method: 'DELETE',
    signal,
  });
};

export const getDeleteMediaCollectionMutationKey = () =>
  ['deleteMediaCollection'] as const;

export const getDeleteMediaCollectionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMediaCollection>>,
    TError,
    DeleteMediaCollectionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMediaCollection>>,
  TError,
  DeleteMediaCollectionMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteMediaCollectionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMediaCollection>>,
    DeleteMediaCollectionMutationVariables
  > = (props) => {
    const { id, collectionId } = props ?? {};

    return deleteMediaCollection(id, collectionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMediaCollectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMediaCollection>>
>;

export type DeleteMediaCollectionMutationError = ErrorType<ApiErrorDto>;
export type DeleteMediaCollectionMutationVariables = { id: string; collectionId: string };

/**
 * @summary Delete a collection. Its assets stay in the library.
 */
export const useDeleteMediaCollection = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteMediaCollection>>,
      TError,
      DeleteMediaCollectionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteMediaCollection>>,
  TError,
  DeleteMediaCollectionMutationVariables,
  TContext
> => {
  return useMutation(getDeleteMediaCollectionMutationOptions(options), queryClient);
};
/**
 * @summary What a collection holds, a page at a time: a smart one's matches, in its order.
 */
export const listMediaCollectionEntries = (
  id: string,
  collectionId: string,
  params?: ListMediaCollectionEntriesParams,
  signal?: AbortSignal,
) => {
  return customInstance<MediaCollectionEntriesDto>({
    url: `/api/v1/workspaces/${id}/media-collections/${collectionId}/entries`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListMediaCollectionEntriesQueryKey = (
  id: string,
  collectionId: string,
  params?: ListMediaCollectionEntriesParams,
) => {
  return [
    `/api/v1/workspaces/${id}/media-collections/${collectionId}/entries`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListMediaCollectionEntriesQueryOptions = <
  TData = Awaited<ReturnType<typeof listMediaCollectionEntries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  collectionId: string,
  params?: ListMediaCollectionEntriesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listMediaCollectionEntries>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListMediaCollectionEntriesQueryKey(id, collectionId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMediaCollectionEntries>>
  > = ({ signal }) => listMediaCollectionEntries(id, collectionId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null &&
      id !== undefined &&
      collectionId !== null &&
      collectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMediaCollectionEntries>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListMediaCollectionEntriesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMediaCollectionEntries>>
>;
export type ListMediaCollectionEntriesQueryError = ErrorType<ApiErrorDto>;

export function useListMediaCollectionEntries<
  TData = Awaited<ReturnType<typeof listMediaCollectionEntries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  collectionId: string,
  params: undefined | ListMediaCollectionEntriesParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listMediaCollectionEntries>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMediaCollectionEntries>>,
          TError,
          Awaited<ReturnType<typeof listMediaCollectionEntries>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMediaCollectionEntries<
  TData = Awaited<ReturnType<typeof listMediaCollectionEntries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  collectionId: string,
  params?: ListMediaCollectionEntriesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listMediaCollectionEntries>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMediaCollectionEntries>>,
          TError,
          Awaited<ReturnType<typeof listMediaCollectionEntries>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMediaCollectionEntries<
  TData = Awaited<ReturnType<typeof listMediaCollectionEntries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  collectionId: string,
  params?: ListMediaCollectionEntriesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listMediaCollectionEntries>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary What a collection holds, a page at a time: a smart one's matches, in its order.
 */

export function useListMediaCollectionEntries<
  TData = Awaited<ReturnType<typeof listMediaCollectionEntries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  collectionId: string,
  params?: ListMediaCollectionEntriesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listMediaCollectionEntries>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListMediaCollectionEntriesQueryOptions(
    id,
    collectionId,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Save what a collection holds today as a new hand-picked collection.
 */
export const copyMediaCollection = (
  id: string,
  collectionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<MediaCollectionDto>({
    url: `/api/v1/workspaces/${id}/media-collections/${collectionId}/copy`,
    method: 'POST',
    signal,
  });
};

export const getCopyMediaCollectionMutationKey = () => ['copyMediaCollection'] as const;

export const getCopyMediaCollectionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof copyMediaCollection>>,
    TError,
    CopyMediaCollectionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof copyMediaCollection>>,
  TError,
  CopyMediaCollectionMutationVariables,
  TContext
> => {
  const mutationKey = getCopyMediaCollectionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof copyMediaCollection>>,
    CopyMediaCollectionMutationVariables
  > = (props) => {
    const { id, collectionId } = props ?? {};

    return copyMediaCollection(id, collectionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type CopyMediaCollectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof copyMediaCollection>>
>;

export type CopyMediaCollectionMutationError = ErrorType<ApiErrorDto>;
export type CopyMediaCollectionMutationVariables = { id: string; collectionId: string };

/**
 * @summary Save what a collection holds today as a new hand-picked collection.
 */
export const useCopyMediaCollection = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof copyMediaCollection>>,
      TError,
      CopyMediaCollectionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof copyMediaCollection>>,
  TError,
  CopyMediaCollectionMutationVariables,
  TContext
> => {
  return useMutation(getCopyMediaCollectionMutationOptions(options), queryClient);
};
/**
 * @summary Add assets to a hand-picked collection; ones already in it are skipped.
 */
export const addMediaCollectionItems = (
  id: string,
  collectionId: string,
  addMediaCollectionItemsDto: BodyType<AddMediaCollectionItemsDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaCollectionDto>({
    url: `/api/v1/workspaces/${id}/media-collections/${collectionId}/items`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: addMediaCollectionItemsDto,
    signal,
  });
};

export const getAddMediaCollectionItemsMutationKey = () =>
  ['addMediaCollectionItems'] as const;

export const getAddMediaCollectionItemsMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addMediaCollectionItems>>,
    TError,
    AddMediaCollectionItemsMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addMediaCollectionItems>>,
  TError,
  AddMediaCollectionItemsMutationVariables,
  TContext
> => {
  const mutationKey = getAddMediaCollectionItemsMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addMediaCollectionItems>>,
    AddMediaCollectionItemsMutationVariables
  > = (props) => {
    const { id, collectionId, data } = props ?? {};

    return addMediaCollectionItems(id, collectionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddMediaCollectionItemsMutationResult = NonNullable<
  Awaited<ReturnType<typeof addMediaCollectionItems>>
>;
export type AddMediaCollectionItemsMutationBody = BodyType<AddMediaCollectionItemsDto>;
export type AddMediaCollectionItemsMutationError = ErrorType<ApiErrorDto>;
export type AddMediaCollectionItemsMutationVariables = {
  id: string;
  collectionId: string;
  data: BodyType<AddMediaCollectionItemsDto>;
};

/**
 * @summary Add assets to a hand-picked collection; ones already in it are skipped.
 */
export const useAddMediaCollectionItems = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof addMediaCollectionItems>>,
      TError,
      AddMediaCollectionItemsMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof addMediaCollectionItems>>,
  TError,
  AddMediaCollectionItemsMutationVariables,
  TContext
> => {
  return useMutation(getAddMediaCollectionItemsMutationOptions(options), queryClient);
};
/**
 * @summary Take an asset out of a hand-picked collection.
 */
export const removeMediaCollectionItem = (
  id: string,
  collectionId: string,
  assetId: string,
  signal?: AbortSignal,
) => {
  return customInstance<MediaCollectionDto>({
    url: `/api/v1/workspaces/${id}/media-collections/${collectionId}/items/${assetId}`,
    method: 'DELETE',
    signal,
  });
};

export const getRemoveMediaCollectionItemMutationKey = () =>
  ['removeMediaCollectionItem'] as const;

export const getRemoveMediaCollectionItemMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeMediaCollectionItem>>,
    TError,
    RemoveMediaCollectionItemMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeMediaCollectionItem>>,
  TError,
  RemoveMediaCollectionItemMutationVariables,
  TContext
> => {
  const mutationKey = getRemoveMediaCollectionItemMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeMediaCollectionItem>>,
    RemoveMediaCollectionItemMutationVariables
  > = (props) => {
    const { id, collectionId, assetId } = props ?? {};

    return removeMediaCollectionItem(id, collectionId, assetId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveMediaCollectionItemMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeMediaCollectionItem>>
>;

export type RemoveMediaCollectionItemMutationError = ErrorType<ApiErrorDto>;
export type RemoveMediaCollectionItemMutationVariables = {
  id: string;
  collectionId: string;
  assetId: string;
};

/**
 * @summary Take an asset out of a hand-picked collection.
 */
export const useRemoveMediaCollectionItem = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof removeMediaCollectionItem>>,
      TError,
      RemoveMediaCollectionItemMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof removeMediaCollectionItem>>,
  TError,
  RemoveMediaCollectionItemMutationVariables,
  TContext
> => {
  return useMutation(getRemoveMediaCollectionItemMutationOptions(options), queryClient);
};
/**
 * @summary Take assets out of a hand-picked collection; ones not in it are skipped.
 */
export const removeMediaCollectionItems = (
  id: string,
  collectionId: string,
  addMediaCollectionItemsDto: BodyType<AddMediaCollectionItemsDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MediaCollectionDto>({
    url: `/api/v1/workspaces/${id}/media-collections/${collectionId}/items/remove`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: addMediaCollectionItemsDto,
    signal,
  });
};

export const getRemoveMediaCollectionItemsMutationKey = () =>
  ['removeMediaCollectionItems'] as const;

export const getRemoveMediaCollectionItemsMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeMediaCollectionItems>>,
    TError,
    RemoveMediaCollectionItemsMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeMediaCollectionItems>>,
  TError,
  RemoveMediaCollectionItemsMutationVariables,
  TContext
> => {
  const mutationKey = getRemoveMediaCollectionItemsMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeMediaCollectionItems>>,
    RemoveMediaCollectionItemsMutationVariables
  > = (props) => {
    const { id, collectionId, data } = props ?? {};

    return removeMediaCollectionItems(id, collectionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveMediaCollectionItemsMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeMediaCollectionItems>>
>;
export type RemoveMediaCollectionItemsMutationBody = BodyType<AddMediaCollectionItemsDto>;
export type RemoveMediaCollectionItemsMutationError = ErrorType<ApiErrorDto>;
export type RemoveMediaCollectionItemsMutationVariables = {
  id: string;
  collectionId: string;
  data: BodyType<AddMediaCollectionItemsDto>;
};

/**
 * @summary Take assets out of a hand-picked collection; ones not in it are skipped.
 */
export const useRemoveMediaCollectionItems = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof removeMediaCollectionItems>>,
      TError,
      RemoveMediaCollectionItemsMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof removeMediaCollectionItems>>,
  TError,
  RemoveMediaCollectionItemsMutationVariables,
  TContext
> => {
  return useMutation(getRemoveMediaCollectionItemsMutationOptions(options), queryClient);
};
